(() => {
  if (customElements.get('free-shipping-component')) return;

  const cartEvents = ['cart-update', 'cart:updated', 'cart:refresh'];

  function isCartMutation(entry) {
    if (entry.initiatorType !== 'fetch' && entry.initiatorType !== 'xmlhttprequest') return false;
    try {
      const url = new URL(entry.name, window.location.href);
      return url.origin === window.location.origin &&
        /\/cart\/(add|change|update|clear)(\.js)?\/?$/.test(url.pathname);
    } catch (error) {
      return false;
    }
  }

  class FreeShippingMeter extends HTMLElement {
    constructor() {
      super();
      this._revision = 0;
      this._timer = null;
      this._request = null;
      this._onCartUpdate = this.scheduleUpdate.bind(this);
      this._observer = new MutationObserver(() => {
        // Refresh only when the component's containers are replaced. Changes to
        // our message, percentage, or an app's money formatting are not cart updates.
        if (this.cacheElements()) this.scheduleUpdate();
      });
      // Apps may update Shopify's cart without emitting the theme's events.
      // Observe completed cart mutations without replacing fetch/XHR or reading
      // app responses. Our own /cart.js reads never match, so cannot feed a loop.
      this._cartObserver = typeof PerformanceObserver === 'function' &&
        PerformanceObserver.supportedEntryTypes?.includes('resource')
        ? new PerformanceObserver(list => {
          if (list.getEntries().some(isCartMutation)) this.scheduleUpdate();
        }) : null;
    }

    connectedCallback() {
      this.cacheElements();
      this.addEventListener('change', this._onCartUpdate);
      this.addEventListener('input', this._onCartUpdate);
      for (const event of cartEvents) {
        document.addEventListener(event, this._onCartUpdate);
        window.addEventListener(event, this._onCartUpdate);
      }
      this._observer.observe(this, { childList: true, subtree: true });
      // No buffered history: a newly connected widget already fetches once.
      this._cartObserver?.observe({ entryTypes: ['resource'] });
      this.scheduleUpdate();
    }

    disconnectedCallback() {
      this._revision += 1;
      clearTimeout(this._timer);
      this._timer = null;
      this._request?.abort();
      this._request = null;
      this._observer.disconnect();
      this._cartObserver?.disconnect();
      this.removeEventListener('change', this._onCartUpdate);
      this.removeEventListener('input', this._onCartUpdate);
      for (const event of cartEvents) {
        document.removeEventListener(event, this._onCartUpdate);
        window.removeEventListener(event, this._onCartUpdate);
      }
    }

    cacheElements() {
      const progressBar = this.querySelector('[data-shipping-progress]');
      const messageElement = this.querySelector('[data-shipping-message]');
      const progressMeter = this.querySelector('[data-free-shipping-progress-meter]');
      const changed = progressBar !== this.progressBar ||
        messageElement !== this.messageElement || progressMeter !== this.progressMeter;
      this.progressBar = progressBar;
      this.messageElement = messageElement;
      this.progressMeter = progressMeter;
      return changed;
    }

    scheduleUpdate() {
      if (!this.isConnected) return;
      // Invalidate old responses immediately, including during the debounce delay.
      this._revision += 1;
      this._request?.abort();
      clearTimeout(this._timer);
      this._timer = setTimeout(() => {
        this._timer = null;
        this.updateFromCart();
      }, 120);
    }

    getGoalCents() {
      // Liquid converts the theme setting from currency units to cents once.
      const goal = Number(this.dataset.goalCents);
      return Number.isSafeInteger(goal) && goal > 0 ? goal : null;
      }

    async updateFromCart() {
      if (!this.isConnected) return;
      this.cacheElements();
      if (!this.progressBar || !this.progressMeter || !this.messageElement) return;

      const goal = this.getGoalCents();
      if (!goal) {
        this.style.display = 'none';
        return;
      }

      const revision = ++this._revision;
      this._request?.abort();
      const request = new AbortController();
      this._request = request;
      const root = (window.Shopify?.routes?.root || '/').replace(/\/?$/, '/');
      try {
        const response = await fetch(this.dataset.cartUrl || `${root}cart.js`, {
          credentials: 'same-origin',
          cache: 'no-store',
          signal: request.signal
        });
        if (!response.ok) throw new Error('Cart request failed');
        const cart = await response.json();
        if (!this.isConnected || revision !== this._revision || request.signal.aborted) return;
        if (!Number.isSafeInteger(cart.total_price) || cart.total_price < 0) {
          throw new Error('Invalid cart total');
        }

        const currency = cart.currency || this.dataset.goalCurrency;
        // The setting is in shop currency. Do not advertise an unconverted
        // threshold for a different Shopify Markets presentment currency.
        if (currency !== this.dataset.goalCurrency) {
          this.style.display = 'none';
          return;
        }
        this.renderCart(cart.total_price, goal, currency);
      } catch (error) {
        if (this.isConnected && revision === this._revision && !request.signal.aborted) {
          // A failed request must not leave a stale eligibility claim or retry loop.
          this.style.display = 'none';
        }
      } finally {
        if (this._request === request) this._request = null;
      }
    }

    formatMoney(cents, currency) {
      return new Intl.NumberFormat(document.documentElement.lang || undefined, {
        style: 'currency', currency
      }).format(cents / 100);
    }

    renderCart(total, goal, currency) {
      this.cacheElements();
      if (!this.progressBar || !this.progressMeter || !this.messageElement) return;
      const percent = Math.min(100, total * 100 / goal);
      const eligible = total >= goal;
      this.freeShippingEligible = eligible ? 1 : 0;
      const label = total === 0 ? 'none' : eligible ? 'progress-free' :
        percent <= 30 ? 'progress-30' : percent <= 60 ? 'progress-60' : 'progress-100';
      this.progressBar.classList.remove('none', 'progress-30', 'progress-60', 'progress-100', 'progress-free');
      this.progressBar.classList.add(label);
      this.progressBar.classList.toggle('progress-hidden', total === 0);
      this.progressMeter.style.width = `${percent}%`;

      const percentage = this.progressMeter.querySelector('.text');
      if (percentage && this.progressBar.dataset.textEnabled === 'true') {
        const value = `${Math.round(percent)}%`;
        if (percentage.textContent !== value) percentage.textContent = value;
      }

      let text;
      let amount;
      if (eligible) {
        text = this.dataset.messageQualified;
      } else if (total === 0) {
        amount = this.formatMoney(goal, currency);
        text = `${this.dataset.messageEmpty} ${amount}`;
      } else {
        amount = this.formatMoney(goal - total, currency);
        text = `${this.dataset.messagePrefix} ${amount} ${this.dataset.messageAway} ${this.dataset.messageFree}`;
      }

      if (this.messageElement.textContent !== text) {
        if (amount) {
          const span = document.createElement('span');
          span.className = 'text';
          span.textContent = amount;
          const prefix = total === 0 ? this.dataset.messageEmpty : this.dataset.messagePrefix;
          const suffix = total === 0 ? '' : ` ${this.dataset.messageAway} ${this.dataset.messageFree}`;
          this.messageElement.replaceChildren(document.createTextNode(`${prefix} `), span, document.createTextNode(suffix));
        } else {
          this.messageElement.textContent = text;
        }
      }

      const progress = this.querySelector('[role="progressbar"]');
      if (progress) {
        progress.setAttribute('aria-valuenow', String(Math.round(percent)));
        progress.setAttribute('aria-valuetext', text);
      }
      this.style.display = 'block';
    }
  }

  // Deferred scripts and later section/app insertions both upgrade immediately.
  customElements.define('free-shipping-component', FreeShippingMeter);
})();
